import { useMemo } from "react"
import { shallowEqual } from "react-redux"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { TrackingProperties } from "connected-components/analytics"
import {
  DEFAULT_GRAPH_STATE,
  GRAPH_PREFERENCE_BASE,
  selectGraphPanel,
} from "connected-components/dashboard/store/graph-slice"
import { isContentSetTemplateId } from "../hooks/use-graph-data"
import { graphFactorSensitivityTemplateKey } from "../templates/template-chart-config"
import type { SensitivityType } from "../templates/factor-sensitivity/types"

// Record<SensitivityType, true> keeps this exhaustive — a new SensitivityType fails to typecheck until added here.
const ALL_SENSITIVITY_TYPES: Record<SensitivityType, true> = { historical: true, predicted: true }
const FACTOR_SENSITIVITY_TEMPLATE_IDS: readonly string[] =
  (Object.keys(ALL_SENSITIVITY_TYPES) as SensitivityType[]).map(graphFactorSensitivityTemplateKey)

// Hand-copied (not imported from NAMED_TEMPLATES, which pulls in heavy UI/resource deps) — tested for parity against the real id list instead.
export const KNOWN_GRAPH_TEMPLATE_IDS = new Set(["performance", "risk", "exposure", "composition", "beta"])

type TemplateClassification =
  | { kind: "factor-sensitivity" }
  | { kind: "content-set" }
  | { kind: "known" }
  | { kind: "custom" }

// Shared by getGraphSurface and getLowCardinalityTemplateId so the branches live in one place.
function classifyTemplate(selectedTemplate: string): TemplateClassification {
  if (FACTOR_SENSITIVITY_TEMPLATE_IDS.includes(selectedTemplate)) return { kind: "factor-sensitivity" }
  if (isContentSetTemplateId(selectedTemplate)) return { kind: "content-set" }
  if (KNOWN_GRAPH_TEMPLATE_IDS.has(selectedTemplate)) return { kind: "known" }
  return { kind: "custom" }
}

// Matches the "graph.<template>" shape dashboard/drilldown/types.ts's DrilldownSurface uses for sourceSurface.
export function getGraphSurface(selectedTemplate: string): string {
  switch (classifyTemplate(selectedTemplate).kind) {
    case "factor-sensitivity": return "graph.factor-sensitivity"
    case "content-set": return "graph.content-set"
    case "known": return `graph.${selectedTemplate}`
    case "custom": return "graph.custom"
  }
}

// Same collapsing as getGraphSurface — otherwise a custom/content-set id leaks through this sibling field.
function getLowCardinalityTemplateId(selectedTemplate: string): string {
  switch (classifyTemplate(selectedTemplate).kind) {
    case "factor-sensitivity": return selectedTemplate
    case "content-set": return "content-set"
    case "known": return selectedTemplate
    case "custom": return "custom"
  }
}

/** Tracked properties for the "Graph" analytics scope — same pattern as KPIAnalyticsProperties. */
export interface GraphAnalyticsProperties extends TrackingProperties {
  /** Which graph template is active, as a low-cardinality discriminator for Mixpanel filtering. */
  surface: string
  /** Collapsed the same way as `surface` — never the raw per-user custom/content-set id. */
  selectedTemplate: string
  /** Count, not the selector list itself — keeps the payload flat and low-cardinality. */
  addedMetricsCount: number
}

// Selects primitives (shallowEqual'd), not the whole panel slot, so unrelated slot writes don't re-render consumers.
export function useGraphAnalyticsProperties(): GraphAnalyticsProperties {
  const dashboardId = useDashboardId()
  const panelId = usePanelId() ?? GRAPH_PREFERENCE_BASE
  const { selectedTemplate, addedMetricsCount } = useAppSelector((s) => {
    const panel = selectGraphPanel(s, dashboardId, panelId)
    return {
      selectedTemplate: panel?.selectedTemplate ?? DEFAULT_GRAPH_STATE.selectedTemplate,
      addedMetricsCount: panel?.addedMetrics.length ?? DEFAULT_GRAPH_STATE.addedMetrics.length,
    }
  }, shallowEqual)

  // Memoize on primitives only so the tracked-properties object stays referentially stable.
  return useMemo<GraphAnalyticsProperties>(
    () => ({
      surface: getGraphSurface(selectedTemplate),
      selectedTemplate: getLowCardinalityTemplateId(selectedTemplate),
      addedMetricsCount,
    }),
    [selectedTemplate, addedMetricsCount],
  )
}
